/**
 * Favorite toggle button for following SEC filers.
 * Heart icon: filled when favorited, outline when not.
 *
 * Attributes: cik (required), name (optional, for POST body)
 * Hidden for anonymous users (no userId = no favorites).
 */

import { icon } from "../../../shared/icons.js";
import { getTier } from "../../lib/tier.js";

interface Favorite {
  cik: string;
  company_name: string;
}

// Module-level cache shared across all instances on the page.
// Avoids re-fetching when multiple buttons mount simultaneously.
let favoritesCache: Map<string, Favorite> | null = null;
let fetchPromise: Promise<Map<string, Favorite>> | null = null;

async function loadFavorites(): Promise<Map<string, Favorite>> {
  if (favoritesCache) return favoritesCache;
  if (fetchPromise) return fetchPromise;

  fetchPromise = fetch("/api/v1/favorites")
    .then((res) => {
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json() as Promise<{ favorites: Favorite[] }>;
    })
    .then(({ favorites }) => {
      const map = new Map<string, Favorite>();
      for (const f of favorites) map.set(f.cik, f);
      favoritesCache = map;
      fetchPromise = null;
      return map;
    })
    .catch(() => {
      fetchPromise = null;
      return new Map<string, Favorite>();
    });

  return fetchPromise;
}

function invalidateCache(): void {
  favoritesCache = null;
  fetchPromise = null;
}

class FtFavoriteButton extends HTMLElement {
  private abortController = new AbortController();
  private favorited = false;
  private loading = false;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  connectedCallback(): void {
    const tier = getTier();
    if (tier === "anonymous") {
      this.style.display = "none";
      return;
    }

    this.render();
    this.checkFavoriteState();
  }

  disconnectedCallback(): void {
    this.abortController.abort();
  }

  private get cik(): string {
    return this.getAttribute("cik") || "";
  }

  private get companyName(): string {
    return this.getAttribute("name") || "";
  }

  private render(): void {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(STYLES);

    const template = document.createElement("template");
    template.innerHTML = `
      <button class="favorite-btn" type="button" aria-label="Add to favorites" aria-pressed="false">
        <span class="icon">${icon("heart", 18)}</span>
      </button>
    `;

    if (this.shadowRoot) {
      this.shadowRoot.innerHTML = "";
      this.shadowRoot.adoptedStyleSheets = [sheet];
      this.shadowRoot.appendChild(template.content.cloneNode(true));
      this.setupEventListeners();
    }
  }

  private async checkFavoriteState(): Promise<void> {
    const favorites = await loadFavorites();
    this.favorited = favorites.has(this.cik);
    this.updateVisual();
  }

  private updateVisual(): void {
    const btn = this.shadowRoot?.querySelector("button");
    if (!btn) return;

    btn.setAttribute("aria-pressed", String(this.favorited));
    btn.setAttribute(
      "aria-label",
      this.favorited ? "Remove from favorites" : "Add to favorites",
    );
    btn.classList.toggle("active", this.favorited);

    const iconEl = btn.querySelector(".icon");
    if (iconEl) {
      iconEl.innerHTML = this.favorited
        ? icon("heart", 18).replace("<svg ", '<svg fill="currentColor" ')
        : icon("heart", 18);
    }
  }

  private setupEventListeners(): void {
    this.abortController.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.shadowRoot
      ?.querySelector("button")
      ?.addEventListener("click", () => this.toggle(), { signal });
  }

  private async toggle(): Promise<void> {
    if (this.loading || !this.cik) return;
    this.loading = true;

    // Optimistic update
    this.favorited = !this.favorited;
    this.updateVisual();

    try {
      if (this.favorited) {
        const res = await fetch("/api/v1/favorites", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ciks: [{ cik: this.cik, name: this.companyName }],
          }),
        });
        if (!res.ok) throw new Error(`${res.status}`);
      } else {
        const res = await fetch(`/api/v1/favorites/${this.cik}`, {
          method: "DELETE",
        });
        if (!res.ok) throw new Error(`${res.status}`);
      }
      invalidateCache();
    } catch {
      // Revert on failure
      this.favorited = !this.favorited;
      this.updateVisual();
    } finally {
      this.loading = false;
    }
  }
}

const STYLES = `
  :host {
    display: inline-flex;
    align-items: center;
  }

  .favorite-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: var(--height-sm, 32px);
    height: var(--height-sm, 32px);
    padding: 0;
    border: 1px solid var(--border, #e5e7eb);
    border-radius: var(--radius, 8px);
    background: transparent;
    color: var(--text-muted, #6b7280);
    cursor: pointer;
    transition: color 200ms, border-color 200ms;
  }

  .favorite-btn:hover {
    color: var(--danger, #ef4444);
    border-color: var(--danger, #ef4444);
  }

  .favorite-btn.active {
    color: var(--danger, #ef4444);
    border-color: var(--danger, #ef4444);
  }

  .favorite-btn:focus-visible {
    outline: 2px solid var(--focus, #2563eb);
    outline-offset: 2px;
  }

  .icon {
    display: inline-flex;
    line-height: 0;
  }

  .icon svg {
    width: 18px;
    height: 18px;
  }

  @media (prefers-color-scheme: dark) {
    .favorite-btn {
      border-color: var(--border, #374151);
      color: var(--text-muted, #9ca3af);
    }
  }
`;

if (!customElements.get("ft-favorite-button")) {
  customElements.define("ft-favorite-button", FtFavoriteButton);
}
